/**
 * src/components/photos/RegionCrop.jsx — resolution-independent crop of a photo
 * around a normalized mark region (TICKET-070E).
 *
 * "A mark is usually a small crop of a big image." This renders JUST that crop —
 * the lightbox uses it for in-place zoom-to-mark (E); the service-order report
 * uses it for per-mark annotation crops (F). Both feed off the same crop window
 * from src/lib/photos/cropToRegion.js, so zoom and report always agree.
 *
 * Two render modes:
 *   - default: an overflow-hidden frame whose aspect = the crop's pixel aspect,
 *     holding a scaled+translated <img> (the natural aspect is learned on load).
 *   - cover (TICKET-070G): the crop COVERS a FIXED-aspect box (coverAspect, e.g.
 *     4/3) undistorted — used by the document so every per-mark card is uniform.
 *
 * TICKET-216a (distortion fix): fill modes need the image's NATURAL aspect or
 * they stretch. natAspect now comes from (1) caller props naturalWidth/Height
 * (no flash), (2) a synchronous read for cached images that never fire onLoad,
 * (3) onLoad. While the aspect is still unknown we render objectFit:contain
 * (letterbox) — NEVER fill — so the image is never deformed.
 *
 * Design tokens only; testids throughout.
 */
import { useState, useRef, useLayoutEffect } from 'react';
import { cropToRegion } from '../../lib/photos/cropToRegion';

export default function RegionCrop({
  imageUrl,
  region = null,
  crop: cropProp = null,
  alt = '',
  maxHeight = '60vh',
  rounded = true,
  cropOpts,
  cover = false,
  coverAspect = 4 / 3,
  fit = false,
  naturalWidth = 0,
  naturalHeight = 0,
}) {
  // naturalWidth/naturalHeight ratio. Prefer caller-provided dims (no flash).
  const [natAspect, setNatAspect] = useState(
    naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : null,
  );
  const imgRef = useRef(null);

  // Cached/complete images frequently do NOT fire onLoad in React, leaving
  // natAspect null forever (the persistent distortion). Read it synchronously
  // before paint. TICKET-216a.
  useLayoutEffect(() => {
    if (natAspect != null) return;
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNatAspect(img.naturalWidth / img.naturalHeight);
    }
  }, [natAspect]);

  const crop = cropProp || cropToRegion(region, cropOpts || {});
  if (!imageUrl) return null;

  // The fit/cover/default modes scale an <img> with explicit width/height; while
  // the natural aspect is unknown, contain (letterbox) avoids any deformation.
  const fillFit = natAspect != null ? 'fill' : 'contain';

  // No usable region → show the whole image (graceful fallback).
  if (!crop) {
    return (
      <div data-testid="region-crop" data-cropped="false" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <img
          ref={imgRef}
          data-testid="region-crop-img"
          src={imageUrl}
          alt={alt}
          draggable={false}
          onLoad={(e) => { const w = e.target?.naturalWidth || 0; const h = e.target?.naturalHeight || 0; if (w > 0 && h > 0) setNatAspect(w / h); }}
          style={{ maxWidth: '100%', maxHeight, objectFit: 'contain', borderRadius: rounded ? 8 : 0, userSelect: 'none' }}
        />
      </div>
    );
  }

  const onLoad = (e) => {
    const w = e.target?.naturalWidth || 0;
    const h = e.target?.naturalHeight || 0;
    if (w > 0 && h > 0) setNatAspect(w / h);
  };

  // COVER mode: the crop fills a FIXED-aspect box, undistorted (TICKET-070G).
  if (cover) {
    const A = natAspect && natAspect > 0 ? natAspect : 1; // image natural aspect
    const Abox = coverAspect > 0 ? coverAspect : 4 / 3;
    // Scale (as a fraction of the box) so the crop covers the box in both axes.
    const imgWf = Math.max(1 / crop.w, A / (crop.h * Abox)); // of box width
    const imgHf = (imgWf * Abox) / A;                        // of box height
    const left = 50 - (crop.x + crop.w / 2) * imgWf * 100;
    const top = 50 - (crop.y + crop.h / 2) * imgHf * 100;
    return (
      <div
        data-testid="region-crop"
        data-cropped="cover"
        style={{
          position: 'relative', width: '100%', aspectRatio: `${Abox}`,
          overflow: 'hidden', borderRadius: rounded ? 8 : 0, background: 'var(--surface-muted)',
        }}
      >
        <img
          ref={imgRef}
          data-testid="region-crop-img"
          src={imageUrl}
          alt={alt}
          draggable={false}
          onLoad={onLoad}
          style={{
            position: 'absolute', left: `${left}%`, top: `${top}%`,
            width: `${imgWf * 100}%`, height: `${imgHf * 100}%`,
            objectFit: fillFit, userSelect: 'none',
          }}
        />
      </div>
    );
  }

  // FIT mode (TICKET-070G): the WHOLE crop, undistorted, at its OWN aspect but
  // HEIGHT-BOUNDED so a narrow/long box never blows up the layout.
  if (fit) {
    const af = natAspect && natAspect > 0 ? natAspect : crop.w / crop.h;
    const frameAspectFit = (crop.w / crop.h) * af;
    const mh = typeof maxHeight === 'number' ? maxHeight + 'px' : maxHeight;
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }} data-testid="region-crop" data-cropped="fit">
        <div
          style={{
            position: 'relative', aspectRatio: `${frameAspectFit}`,
            width: `min(100%, calc(${mh} * ${frameAspectFit}))`,
            overflow: 'hidden', borderRadius: rounded ? 8 : 0, background: 'var(--glass)',
          }}
        >
          <img
            ref={imgRef}
            data-testid="region-crop-img"
            src={imageUrl}
            alt={alt}
            draggable={false}
            onLoad={onLoad}
            style={{
              position: 'absolute', top: 0, left: 0,
              width: `${100 / crop.w}%`, height: `${100 / crop.h}%`,
              objectFit: fillFit,
              transform: `translate(${-crop.x * 100}%, ${-crop.y * 100}%)`,
              userSelect: 'none',
            }}
          />
        </div>
      </div>
    );
  }

  // frame aspect (w:h) = crop.w·naturalW / (crop.h·naturalH). With a square-image
  // fallback before the natural size is known, then exact after onLoad.
  const a = natAspect && natAspect > 0 ? natAspect : crop.w / crop.h;
  const frameAspect = (crop.w / crop.h) * a;

  return (
    <div
      data-testid="region-crop"
      data-cropped="true"
      style={{
        position: 'relative', width: '100%', aspectRatio: `${frameAspect}`,
        overflow: 'hidden', borderRadius: rounded ? 8 : 0, background: 'var(--glass)',
      }}
    >
      <img
        ref={imgRef}
        data-testid="region-crop-img"
        src={imageUrl}
        alt={alt}
        draggable={false}
        onLoad={onLoad}
        style={{
          position: 'absolute', top: 0, left: 0,
          width: `${100 / crop.w}%`, height: `${100 / crop.h}%`,
          objectFit: fillFit,
          transform: `translate(${-crop.x * 100}%, ${-crop.y * 100}%)`,
          userSelect: 'none',
        }}
      />
    </div>
  );
}
